// Copyright ©️ 2026 eVolve MEP, LLC

import { Stack, Text, Button, CheckIcon, Flex, type Primitive } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import type { WrappedListProps } from 'components/Mantine/TypeSafeSelect/types';
import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

const ListItem = <T extends Primitive, D extends object = object>({
  item,
  renderOption,
  selected,
  disableOption,
  disabled,
  onChange,
}: Pick<WrappedListProps<T, D>, 'onChange' | 'disableOption' | 'disabled' | 'renderOption'> & {
  item: WrappedListProps<T, D>['data'][number];
  selected?: boolean;
}) => (
  // TODO: Replace with not a Button to give more flexibility to `renderOption`
  <Button
    variant="white"
    color="dark"
    fw="normal"
    onClick={() => onChange(item.value)}
    leftSection={selected ? <CheckIcon size={10} /> : null}
    styles={{
      inner: {
        justifyContent: 'flex-start',
      },
    }}
    disabled={disableOption?.(item.value) || disabled}
  >
    {renderOption?.({
      option: item,
    }) ?? item.label}
  </Button>
);

export const WrappedList = <T extends Primitive, D extends object = object>({
  defaultValue: parentDefaultValue,
  value: parentValue,
  onChange: parentOnChange,
  data,
  entireCount = data.length,
  onOptionSubmit,
  textInputProps,
  flexListProps,
  rightSideComponent,
  searchDisabled,
  searchable = true,
  filterInclude,
  nothingFoundMessage = 'No results found.',
  onScrollToEnd,
  loading,
  disableOption,
  disabled,
  searchValue,
  pendingSearchValue,
  onSearchChange,
  renderOption,
  miw = 250,
}: WrappedListProps<T, D>) => {
  const [searchPhrase, setSearchPhrase] = useUncontrolled({
    value: pendingSearchValue ?? searchValue,
    defaultValue: '',
    onChange: onSearchChange,
  });

  const [value, onChange] = useUncontrolled<T | null>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: (v) => {
      parentOnChange(v);
      if (v) onOptionSubmit?.(v);
    },
  });

  return (
    <Stack gap={0} miw={miw} p={4} style={{ overflow: 'hidden' }}>
      <Flex gap="xs" align="center">
        {searchable && (
          <TextInputWrapped
            autoFocus
            p="xs"
            disabled={searchDisabled || disabled}
            placeholder="Search..."
            flex={1}
            {...textInputProps}
            value={searchPhrase}
            onChange={setSearchPhrase}
          />
        )}
        {rightSideComponent}
      </Flex>
      <Stack flex={1} style={{ overflowY: 'auto' }}>
        <Stack gap={0} {...flexListProps}>
          {data
            .filter((o) => filterInclude?.(o, searchPhrase) ?? true)
            .map((o) => (
              <ListItem
                key={String(o.value)}
                item={o}
                renderOption={renderOption}
                onChange={onChange}
                disableOption={disableOption}
                disabled={disabled}
                selected={o.value === value}
              />
            ))}
          {!loading && entireCount === 0 && (
            <Text c="dimmed" fz="sm" py="sm" px="md" pt={0}>
              {nothingFoundMessage ?? 'No results found.'}
            </Text>
          )}
          {isNotNil(onScrollToEnd) && (
            <TriggerOnVisible
              onVisible={onScrollToEnd}
              loading={loading}
              loaderProps={{ size: 'sm', mx: 'md', mt: 0 }}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
